import { useNavigate } from 'react-router';
import { useParams } from 'react-router-dom';
import React, { useRef, useMemo, useState, useEffect, useCallback } from 'react';

import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Container from '@mui/material/Container';
import Accordion from '@mui/material/Accordion';
import Typography from '@mui/material/Typography';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';

import PDFViewer from './pdf/PDFViewer';
import useAuth from '../../hooks/useAuth';
import { useSelector } from '../../redux/store';
import useLocales from '../../hooks/useLocales';
import FormFiller from './fill-form/FormFiller';
import FormActionButtons from './FormActionButtons';
import type { TempFile } from './FilePreviewSections';
import PDFViewerDocument from './pdf/PDFViewerDocument';
import LoadingScreen from '../../components/LoadingScreen';
import useNotifications from '../../hooks/useNotifications';
import FormSidebarEditDocument from './FormSidebarEditDocument';
import { useFormValidation } from '../../hooks/useFormValidation';
import FormSidebarCreateDocument from './FormSidebarCreateDocument';
import { useParsedFormStructure } from '../../hooks/useParsedFormStructure';
import { fillCollabForm, generateDocument } from '../../services/formService';
import CombinedFormFillingDialog from './fill-form/CombinedFormFillingDialog';
import { useCollaboratorValidation } from '../../hooks/useCollaboratorValidation';
import { DocumentModuleTypes } from '../../@types/shared/enums/documentModuleTypes';
import { getBase64FromFile, getExtensionByFileMediaType } from '../../utils/fileUtils';
import { MAX_DOCUMENT_NAME_LENGTH, MIN_DOCUMENT_NAME_LENGTH } from './constants/constants';
import type {
  FormContent,
  Collaborator,
  FillFormProps,
  PdfOffsetProps,
  FormAttachmentProps,
  FormAccompanyingDocumentProps,
} from '../../@types/forms/types';

// ----------------------------------------------------------------------

const Form = () => {
  const { t } = useLocales();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { folderId, formId, documentId } = useParams();
  const { enqueueSuccess, enqueueError } = useNotifications();
  const [isLoading, setIsLoading] = useState(false);

  // Form data from Redux
  const { form } = useSelector((state) => state.formBuilder);

  // Memoize parsed form structure to avoid re-parsing on every render
  const parsedElements = useParsedFormStructure(form?.structure);

  // State management
  const [collaborators, setCollaborators] = useState<Collaborator[]>([]);
  const [documentName, setDocumentName] = useState<string>('');
  const [content, setContentInternal] = useState<FormContent[]>([]);

  const setContent = useCallback(
    (newContent: FormContent[] | ((prev: FormContent[]) => FormContent[])) => {
      setContentInternal((prev) => {
        const result = typeof newContent === 'function' ? newContent(prev) : newContent;
        return result;
      });
    },
    []
  );

  // Track previous formId to detect when it changes
  const prevFormIdRef = useRef<string | undefined>();

  // Reset state when formId changes (navigating between different forms)
  // Only reset when NOT editing an existing document (documentId is undefined)
  useEffect(() => {
    if (prevFormIdRef.current !== undefined && prevFormIdRef.current !== formId && !documentId) {
      setCollaborators([]);
      setDocumentName('');
      setContent([]);
    }
    prevFormIdRef.current = formId;
  }, [formId, documentId, setContent]);

  // Grid view state
  const [gridView, setGridView] = useState(() => {
    const savedView = localStorage.getItem('formGridView');
    return savedView || 'columns';
  });

  // File upload state (create mode)
  const [mainFile, setMainFile] = useState<File[]>([]);
  const [attachments, setAttachments] = useState<File[]>([]);
  const [accompanyingDocuments, setAccompanyingDocuments] = useState<File[]>([]);
  const [mainFileError, setMainFileError] = useState<boolean>(false);

  // Temp files state (edit mode)
  const [mainTempFiles, setMainTempFiles] = useState<TempFile[]>([]);

  // Combined mode state
  const [isCombinedMode, setIsCombinedMode] = useState<boolean>(false);

  // PDF offset state (for backend coordinate correction)
  const [pdfOffset, setPdfOffset] = useState<PdfOffsetProps>({ xOffset: 0, yOffset: 0 });

  // Derived state
  const isDocument = !!documentId;
  const isGridColumnView = gridView === 'columns';

  // Custom hooks for validation
  const {
    fieldErrors,
    validateFormFields,
    focusFieldId,
    setFocusFieldId,
    handleRequiredFieldClick,
    fieldsGroupedByCollaborator,
  } = useFormValidation({
    formStructure: form?.structure,
    parsedElements,
    content,
    collaborators,
    userEmail: user?.email,
  });

  const { collaboratorsErrors, setCollaboratorsErrors, validateCollaborators } =
    useCollaboratorValidation();

  // Clear main file error when file is uploaded
  useEffect(() => {
    if (mainFile.length > 0 && mainFileError) {
      setMainFileError(false);
    }
  }, [mainFile, mainFileError]);

  // Memoized callbacks
  const handleCombinedModeClick = useCallback(() => {
    setIsCombinedMode(true);
  }, []);

  const handlePdfOffsetChange = useCallback((offset: PdfOffsetProps) => {
    setPdfOffset(offset);
  }, []);

  const handleFocusFieldHandled = useCallback(() => {
    setFocusFieldId(null);
  }, [setFocusFieldId]);

  // Form submission handler
  const handleSubmit = useCallback(
    async (completeForm = false) => {
      if (!form) {
        enqueueError(t('forms.validationErrorMessage.formNotLoaded'));
        return false;
      }

      // Validate document name
      const isDocumentNameValid =
        documentName.length >= MIN_DOCUMENT_NAME_LENGTH &&
        documentName.length <= MAX_DOCUMENT_NAME_LENGTH;

      if (!isDocumentNameValid) {
        enqueueError(t('forms.validationErrorMessage.invalidDocumentName'));
        return false;
      }

      // Validate collaborators (only for Save, not for Generate)
      if (!completeForm) {
        const areValidCollaborators = validateCollaborators(collaborators);
        if (!areValidCollaborators) {
          enqueueError(t('forms.validationErrorMessage.invalidCollaboratorsEmail'));
          return false;
        }
      }

      // Validate main file requirement for Accounting forms
      if (
        !isDocument &&
        form.formModuleType === DocumentModuleTypes.Accounting &&
        mainFile.length === 0
      ) {
        setMainFileError(true);
        enqueueError(t('forms.validationErrorMessage.mainDocumentRequired'));
        return false;
      }

      // Validate form fields
      // completeForm (Generate) = validate all fields, Save = validate only current user's fields
      const areValidFields = validateFormFields(completeForm);
      if (!areValidFields) {
        enqueueError(t('forms.validationErrorMessage.requiredFieldsEmpty'));
        return false;
      }

      try {
        setIsLoading(true);

        const model: FillFormProps = {
          name: documentName,
          structure: form.structure,
          content: JSON.stringify(content),
          formModuleType: form.formModuleType,
          formTypeId: form.formTypeId,
          doclogixCoreProfileId: form.doclogixCoreProfileId,
          doclogixCoreFileId: form.doclogixCoreFileId,
          approvalInDoclogix: form.approvalInDoclogix,
          managerApproval: form.managerApproval,
          ceoApproval: form.ceoApproval,
          attachments: await Promise.all([
            ...mainFile.map(async (a) => {
              const attachment: FormAttachmentProps = {
                name: a.name,
                extension: getExtensionByFileMediaType(a.type),
                main: true,
                generated: true,
                file: await getBase64FromFile(a),
              };
              return attachment;
            }),
            ...attachments.map(async (a) => {
              const attachment: FormAttachmentProps = {
                name: a.name,
                extension: getExtensionByFileMediaType(a.type),
                main: false,
                generated: true,
                file: await getBase64FromFile(a),
              };
              return attachment;
            }),
          ]),
          accompanyingFiles: await Promise.all(
            accompanyingDocuments.map(async (a) => {
              const accompanyingFile: FormAccompanyingDocumentProps = {
                name: a.name,
                extension: a.type,
                file: await getBase64FromFile(a),
              };
              return accompanyingFile;
            })
          ),
          folderId: folderId ? Number(folderId) : undefined,
          formTemplateId: formId ? Number(formId) : undefined,
          collaborators: collaborators.map((c, index) => ({
            ...c,
            order: c.order ?? index,
          })),
          documentId: documentId ? Number(documentId) : undefined,
          offset: pdfOffset,
        };

        if (completeForm) {
          const docId: number = await generateDocument(model);

          navigate(`/standard/documents/${docId}`);
        } else {
          await fillCollabForm(model);

          navigate(`/documents/${Number(folderId)}`);
        }

        enqueueSuccess(
          isDocument
            ? t('forms.successMessage.documentUpdated')
            : t('forms.successMessage.documentCreated')
        );

        setIsLoading(false);

        return true;
      } catch (error) {
        setIsLoading(false);
        console.error('Form submission error:', error);

        return false;
      }
    },
    [
      form,
      documentName,
      collaborators,
      mainFile,
      attachments,
      accompanyingDocuments,
      content,
      folderId,
      formId,
      documentId,
      isDocument,
      pdfOffset,
      validateCollaborators,
      validateFormFields,
      enqueueError,
      enqueueSuccess,
      navigate,
      t,
    ]
  );

  // Memoize FormFiller props to prevent unnecessary re-renders
  const formFillerProps = useMemo(
    () => ({
      formId: formId ? Number(formId) : 0,
      onContentChange: setContent,
      collaborators,
      setCollaborators,
      isDocument,
      documentName,
      setDocumentName,
      fieldErrors,
      focusFieldId,
      onFocusFieldHandled: handleFocusFieldHandled,
      externalContent: content,
      onPdfOffsetChange: handlePdfOffsetChange,
    }),
    [
      formId,
      collaborators,
      isDocument,
      documentName,
      fieldErrors,
      focusFieldId,
      handleFocusFieldHandled,
      handlePdfOffsetChange,
      content,
      setContent,
    ]
  );

  // Render FormFiller based on mode
  const renderFormFiller = useCallback(
    (combinedMode: boolean) => (
      <FormFiller {...formFillerProps} isCombinedMode={combinedMode} canResize={combinedMode} />
    ),
    [formFillerProps]
  );

  return (
    <Container maxWidth={isGridColumnView ? 'xl' : 'lg'}>
      <Grid container spacing={2}>
        {/* Main Content - Form */}
        <Grid size={{ xs: 12, md: 12, lg: isGridColumnView ? 8 : 12 }}>
          <Accordion defaultExpanded sx={{ p: 3 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ p: 3 }}>
              <Typography variant="h6">{t('forms.document')}</Typography>
            </AccordionSummary>

            <AccordionDetails sx={{ p: 3, pt: 0 }}>
              <Box>{formId && renderFormFiller(false)}</Box>
            </AccordionDetails>
          </Accordion>
        </Grid>

        {/* Sidebar - Right Column */}
        {!isDocument && (
          <Grid size={{ xs: 12, md: 12, lg: isGridColumnView ? 4 : 12 }}>
            <FormSidebarCreateDocument
              collaborators={collaborators}
              setCollaborators={setCollaborators}
              collaboratorsErrors={collaboratorsErrors}
              setCollaboratorsErrors={setCollaboratorsErrors}
              fieldsGroupedByCollaborator={fieldsGroupedByCollaborator}
              fieldErrors={fieldErrors}
              onFieldClick={handleRequiredFieldClick}
              isDocument={isDocument}
              formModuleType={form?.formModuleType}
              mainFile={mainFile}
              setMainFile={setMainFile}
              attachments={attachments}
              setAttachments={setAttachments}
              accompanyingDocuments={accompanyingDocuments}
              setAccompanyingDocuments={setAccompanyingDocuments}
              onCombinedModeClick={handleCombinedModeClick}
              mainFileError={mainFileError}
            />
          </Grid>
        )}

        {/* Sidebar - Right Column */}
        {isDocument && (
          <Grid size={{ xs: 12, md: 12, lg: isGridColumnView ? 4 : 12 }}>
            <FormSidebarEditDocument
              collaborators={collaborators}
              setCollaborators={setCollaborators}
              collaboratorsErrors={collaboratorsErrors}
              setCollaboratorsErrors={setCollaboratorsErrors}
              fieldsGroupedByCollaborator={fieldsGroupedByCollaborator}
              fieldErrors={fieldErrors}
              onFieldClick={handleRequiredFieldClick}
              documentId={documentId}
              formModuleType={form?.formModuleType}
              onCombinedModeClick={handleCombinedModeClick}
              setContent={setContent}
              setMainTempFiles={setMainTempFiles}
            />
          </Grid>
        )}
      </Grid>

      {/* Action Buttons */}
      <FormActionButtons
        gridView={gridView}
        setGridView={setGridView}
        handleSubmit={handleSubmit}
        disabled={collaborators.find((c) => c.role === 'owner')?.email !== user?.email}
        saveDisabled={!collaborators.some((c) => c.email === user?.email)}
      />

      {/* Combined Mode Dialog */}
      {formId && (
        <CombinedFormFillingDialog
          isCombinedMode={isCombinedMode}
          setIsCombinedMode={setIsCombinedMode}
          documentName={documentName}
          form={renderFormFiller(true)}
          mainDocument={
            isDocument && mainTempFiles.length > 0 ? (
              <PDFViewerDocument accessId={mainTempFiles[0].accessId} />
            ) : (
              <PDFViewer file={mainFile[0]} />
            )
          }
        />
      )}

      {isLoading && <LoadingScreen />}
    </Container>
  );
};

export default Form;
